
/**
 * Utility functions for form validation with security enhancements
 */

import { useToast } from "@/hooks/use-toast";
import DOMPurify from 'dompurify';

interface ValidationOptions {
  toast: ReturnType<typeof useToast>['toast'];
}

export interface ContactFormData {
  name: string;
  email: string;
  subject: string;
  message: string;
  consent_given: boolean;
}

export interface EmailJSConfig {
  emailjsServiceId: string;
  emailjsTemplateId: string;
  emailjsPublicKey: string;
}

/**
 * Sanitizes user input to prevent XSS attacks
 */
export const sanitizeInput = (input: string): string => {
  return DOMPurify.sanitize(input, { 
    ALLOWED_TAGS: [], // Strip all HTML tags
    ALLOWED_ATTR: [] // Strip all attributes
  }).trim();
};

/**
 * Enhanced email validation with multiple checks
 */
const validateEmail = (email: string): boolean => {
  // Basic format check
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email)) return false;
  
  // Additional security checks
  if (email.length > 254) return false; // RFC 5321 limit
  if (email.includes('..')) return false; // No consecutive dots
  
  return true;
};

export const validateForm = (
  formData: ContactFormData,
  { toast }: ValidationOptions
): boolean => {
  // Sanitize all inputs
  const sanitizedData = {
    name: sanitizeInput(formData.name),
    email: sanitizeInput(formData.email),
    subject: formData.subject ? sanitizeInput(formData.subject) : '',
    message: sanitizeInput(formData.message)
  };

  // Check required fields after sanitization
  if (!sanitizedData.name || !sanitizedData.email || !sanitizedData.message) {
    toast({
      title: "Fehlende Angaben",
      description: "Bitte füllen Sie alle erforderlichen Felder aus.",
      variant: "destructive",
    });
    return false;
  }
  
  if (!formData.consent_given) {
    toast({
      title: "Datenschutzerklärung erforderlich",
      description: "Bitte akzeptieren Sie die Datenschutzerklärung, um fortzufahren.",
      variant: "destructive",
    });
    return false;
  }
  
  // Enhanced email validation
  if (!validateEmail(sanitizedData.email)) {
    toast({
      title: "Ungültige E-Mail-Adresse",
      description: "Bitte geben Sie eine gültige E-Mail-Adresse ein.",
      variant: "destructive",
    });
    return false;
  }

  // Additional length checks for security
  if (sanitizedData.name.length > 100) {
    toast({
      title: "Name zu lang",
      description: "Der Name darf maximal 100 Zeichen lang sein.",
      variant: "destructive",
    });
    return false;
  }

  if (sanitizedData.message.length > 5000) {
    toast({
      title: "Nachricht zu lang",
      description: "Die Nachricht darf maximal 5000 Zeichen lang sein.",
      variant: "destructive",
    });
    return false;
  }
  
  return true;
};

export const validateEmailJSConfig = (
  config: EmailJSConfig,
  { toast }: ValidationOptions
): boolean => {
  if (!config.emailjsServiceId || !config.emailjsTemplateId || !config.emailjsPublicKey) {
    toast({
      title: "Konfigurationsfehler",
      description: "Die E-Mail-Konfiguration ist unvollständig. Bitte kontaktieren Sie uns direkt per E-Mail.",
      variant: "destructive",
    });
    return false;
  }
  return true;
};
